/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router';
import {
    calendarDateToUtcString,
    localToday,
    NotFoundPage,
    SecurityHelper,
    Spinner,
    toTitleCase,
    useBreakpoint,
    useFlags,
    utcStringToCalendarDate,
} from 'sarsaparilla';
import { isMobileApp } from 'sarsaparilla/src/js/helpers/securityHelper';
import { useSendGAEvent } from 'shared-ui/utils/gaCommon';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { INVENTORY_CAMPING } from 'ui-field-sales/src/constants/types';
import { fetchAggregate } from 'ui-rating-review/src/actions/aggregate';
import { calculateAverageCellSignal } from 'ui-rating-review/src/utils/cellCoverage';
import { navigateToSearchResults } from 'ui-search/src/actions/search';
import { addReferrer } from '../../utils/campground';

import { fetchAmenities } from '../../../shared/actions/amenity';
import { fetchCampsites } from '../../../shared/actions/available-campsites';
import {
    fetchCampground,
    fetchCampgroundPills,
    fetchCampgroundRates,
    fetchCampgroundRecreationSites,
    fetchCampgroundSearchResult,
    fetchCampgroundSiteTypesCount,
    fetchReleaseDates,
    fetchSiteSchedules,
} from '../../../shared/actions/campground';
import { formatStateAbbreviations } from '../../../shared/utils/string_utils';

import type { Campground } from '../../../../types/camping';
import { FacilityType } from '../../../../types/enums';
import {
    fetchCampgroundAvailability,
    setAvailabilityQueryEnabled,
} from '../../../shared/actions/availability';
import {
    removeMapCampsiteIdsFilter,
    setGridRange,
} from '../../../shared/actions/availabilityGrid';
import { fetchRecareaName } from '../../../shared/actions/recarea';
import { fetchRentalEquipments } from '../../../shared/actions/rentals';
import { NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX } from '../../../shared/constants';
import { VOYAGEURS_FACILITY_ID } from '../../../shared/constants/campground';
import { useMonthlyAvailability } from '../../../shared/hooks/useMonthlyAvailability';
import { isItineraryFacility } from '../../../shared/utils/campground_utils';
import {
    isDateSameOrAfter,
    isValidUrlDate,
    searchDateToCalendarDate,
} from '../../../shared/utils/dateUtils';
import { getInitialMonthVisible } from '../../../shared/utils/seasonUtils';
import { getCart } from '../../actions/reservations';
import CampgroundPageProvider from '../../context/CampgroundPageContext';
import { useHeroImagePreload } from './useHeroImagePreload';

import CampgroundPageV3Loading from '../../../features/facilityPageV3/components/CampgroundPageV3Loading';
import CampgroundPageLoading from '../../components/campground/placeholders/CampgroundPageLoading';

const importV3 = () =>
    import('../../../features/facilityPageV3/components/CampgroundPageV3');
const importDesktop = () => import('../../components/campground/CampgroundPage');

const CampgroundPageV3 = lazy(importV3);
const CampgroundPageDesktop = lazy(importDesktop);

export default function CampgroundPageContainer() {
    const { campingFilterAsYouMove } = useFlags();
    const { cGrndId: facilityId } = useParams();
    const [searchParams] = useSearchParams();
    const urlStartDate: string | null =
        isValidUrlDate(searchParams.get('startDate') || '') &&
        isDateSameOrAfter(
            searchDateToCalendarDate(searchParams.get('startDate') || ''),
            localToday
        )
            ? searchParams.get('startDate') || ''
            : null;

    const { isSmAndDown: isMobile } = useBreakpoint();
    const dispatch = useDispatch();

    useEffect(() => {
        if (isMobile && !isMobileApp()) {
            importV3();
        } else {
            importDesktop();
        }
    }, [isMobile]);

    useHeroImagePreload(facilityId);

    const [isReleaseDatesLoading, setIsReleaseDatesLoading] = useState(true);
    const item_category = INVENTORY_CAMPING;

    const facilityData = useSelector<any, Campground>((state) =>
        get(state, 'campground.campground.results', {})
    );
    const isFacilityValid = useSelector((state) =>
        get(state, 'campground.campground.isValid', true)
    );
    const campgroundSearchResult = useSelector((state) =>
        get(state, 'campground.campground.searchResult', {})
    );
    const isCampgroundLoading = useSelector((state) =>
        get(state, 'campground.campground.isLoading', false)
    );

    const isCampgroundPageV3 =
        isMobile &&
        !isMobileApp() &&
        facilityData?.facility_type === FacilityType.Standard;

    // Optimistically render V3 on mobile while facility_type is unknown so the
    // preloaded chunk can start mounting before the campground fetch resolves.
    const isFacilityTypeKnown = Boolean(facilityData?.facility_type);
    const renderV3 =
        isMobile && !isMobileApp() && (isCampgroundPageV3 || !isFacilityTypeKnown);

    const isDesktopLayout = !isMobile && !isMobileApp();

    const searchCheckInTime = useSelector((state: any) => state.search.checkin_time);
    const checkInTime = searchCheckInTime || urlStartDate;
    const recAreaData = useSelector((state: any) => state.recarea.results);
    const ratingsData = useSelector(
        (state: any) => state.ratingReviews.fetchAggregate.data
    );
    const seasonData = useSelector((state: any) => state.campground.rates.results);
    const siteTypes = useSelector(
        (state: any) => state.campground.campground.siteTypes.type
    );
    const typeOfUse = useSelector((state: any) => state.campground.campground.typeOfUse);
    const releaseDates = useSelector(
        (state: any) => state.campground.campground.releaseDates
    );
    const searchQuery = useSelector((state: any) => get(state, 'search.what', null));
    const isSeasonValid = useSelector((state: any) => state.campground.rates.isValid);
    const gridSize = useSelector((state: any) =>
        get(state, 'availabilityGrid.gridSize', NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX)
    );
    const gridSizeChanged = useSelector((state) =>
        get(state, 'availabilityGrid.gridSizeChanged')
    );
    const gridStartDate = useSelector((state) =>
        get(state, 'availabilityGrid.gridStartDate')
    );
    const isApplicationDuplicated = useSelector((state) =>
        get(state, 'earlyAccessLottery.errors.duplicated', false)
    );
    const isApplicationInCart = useSelector((state) =>
        get(state, 'earlyAccessLottery.errors.inCart', false)
    );

    const navigateToSearchResultsVar = useCallback(() => {
        dispatch(navigateToSearchResults());
    }, [dispatch]);

    /* Runs before the effects that dispatch fetchCampgroundAvailability so the epic stands
    down before the first request. Reset on unmount - other pages sharing this store
    (order details, internal camping) still fetch through the epic. */
    useEffect(() => {
        dispatch(setAvailabilityQueryEnabled(true));

        return () => {
            dispatch(setAvailabilityQueryEnabled(false));
        };
    }, [dispatch]);

    useMonthlyAvailability({ campgroundId: facilityId });

    const {
        facility_name: facilityName,
        facility_description_map: facilityDescription,
        parent_asset_id: parentId,
        facility_rules: facilityRules,
        is_deactivated: isDeactivated,
        facility_time_zone: facilityTimeZone,
    } = facilityData;

    const {
        isLoading: isRecAreaLoading,
        name: parentName,
        facilityId: reduxParentId,
    } = recAreaData;

    const {
        number_of_ratings: reviewCount,
        average_rating: rating,
        aggregate_cell_coverage_ratings: mobileCoverageRating,
    } = ratingsData;

    let cellAverage = 0;
    if (mobileCoverageRating) {
        cellAverage = calculateAverageCellSignal(mobileCoverageRating);
    }

    let { city, state_code: state } = campgroundSearchResult;
    if (!city || !state) {
        city = get(facilityData, 'city', null);
        state = get(facilityData, 'state', null);
    }
    const isItinerary = isItineraryFacility(facilityData?.facility_type);
    const facilityNearLocation = state && city ? `${city}, ${state}` : null;

    const isLoadingStatuses = useMemo(() => {
        return {
            releaseDates: isReleaseDatesLoading,
            recArea: parentId ? isRecAreaLoading : false,
            campground: isCampgroundLoading,
        };
    }, [isCampgroundLoading, isRecAreaLoading, isReleaseDatesLoading, parentId]);

    /**
     * If there is no redux data OR the redux facilityId doesn't match the URL,
     * fetch all the data except rec area
     */
    useEffect(() => {
        dispatch<any>(fetchCampground({ id: facilityId }));
        dispatch<any>(fetchCampgroundRates({ id: facilityId }));
        dispatch<any>(fetchCampgroundSiteTypesCount(facilityId));
        dispatch<any>(fetchCampgroundPills(facilityId));
        dispatch<any>(fetchCampgroundRecreationSites(facilityId)); // can be moved to facilityInfoTabContainer
        dispatch<any>(fetchReleaseDates(facilityId)).then(() => {
            setIsReleaseDatesLoading(false);
        });
        dispatch<any>(fetchCampgroundSearchResult(facilityId));
        dispatch<any>(fetchCampsites(facilityId, true, { management: true }));
        dispatch<any>(
            fetchAggregate({ locationType: 'Campground', locationId: facilityId })
        );
        dispatch<any>(fetchAmenities(facilityId));
        dispatch<any>(fetchSiteSchedules(facilityId));

        if (SecurityHelper.isLoggedIn()) {
            getCart(dispatch);
        }

        if (facilityId === VOYAGEURS_FACILITY_ID) {
            dispatch(fetchRentalEquipments(facilityId));
        }

        addReferrer({
            entityId: facilityId,
            source: 'campgroundPage',
            bookingVersion: 1,
        });
    }, [dispatch, facilityId]);

    /**
     * Fetch rec area data using the parentId from the facilityData we fetched
     * When the parentId is present AND there is no existing data OR the existing data doesn't match the URL ...
     * fetch the rec area
     */
    useEffect(() => {
        if (parentId && (!reduxParentId || reduxParentId !== parentId)) {
            dispatch<any>(fetchRecareaName(parentId));
        }
    }, [dispatch, parentId, reduxParentId]);

    useEffect(() => {
        if (isSeasonValid && facilityName) {
            const startDate = checkInTime
                ? searchDateToCalendarDate(checkInTime)
                : getInitialMonthVisible(seasonData, facilityTimeZone);

            const endDate = startDate.add({ days: gridSize });

            dispatch<any>(
                setGridRange({
                    startDate: calendarDateToUtcString(startDate)!,
                    endDate: calendarDateToUtcString(endDate)!,
                })
            );

            dispatch(
                fetchCampgroundAvailability({
                    facilityId,
                    startDate: calendarDateToUtcString(startDate)!,
                    endDate: calendarDateToUtcString(endDate),
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSeasonValid, facilityName]);

    // Fire the view_item GA Event
    useSendGAEvent({
        itemLevel: 'Facility',
        facilityId: facilityId || '',
        itemCategory: item_category,
        isDataLoading: isCampgroundLoading || !typeOfUse?.length,
        typeOfUse,
    });

    useEffect(() => {
        if (facilityId && gridSizeChanged && gridStartDate) {
            dispatch(
                fetchCampgroundAvailability({
                    facilityId: facilityId || '',
                    startDate: gridStartDate,
                    endDate: calendarDateToUtcString(
                        utcStringToCalendarDate(gridStartDate).add({ days: gridSize })
                    ),
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridSize, gridSizeChanged]);

    useEffect(() => {
        if (campingFilterAsYouMove !== undefined && !campingFilterAsYouMove) {
            dispatch(removeMapCampsiteIdsFilter());
        }
    }, [campingFilterAsYouMove, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(removeMapCampsiteIdsFilter());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isDeactivated || !isFacilityValid) {
        // isDeactivated means campground exists but unavailable to the public
        // isFacilityValid means campground exists
        const errorBodyText =
            isDeactivated &&
            `Whoops! We could not find the campground you've requested. Please click the button below to go back to the previous page or enter a new search above.`;
        return <NotFoundPage errorBodyText={errorBodyText} />;
    }

    return (
        <CampgroundPageProvider>
            <Suspense
                fallback={
                    renderV3 ? (
                        <CampgroundPageV3Loading />
                    ) : isDesktopLayout ? (
                        <CampgroundPageLoading />
                    ) : (
                        <Spinner isCentered size="lg" />
                    )
                }
            >
                {renderV3 ? (
                    <CampgroundPageV3
                        campgroundId={facilityId || ''}
                        campgroundNearLocation={facilityNearLocation || ''}
                        parentName={parentName || ''}
                        parentUrl={parentId ? getGatewayUrl(parentId) : undefined}
                        reviewCount={reviewCount}
                        releaseDates={releaseDates}
                        siteTypes={siteTypes}
                        typeOfUse={typeOfUse}
                    />
                ) : isDesktopLayout && (isCampgroundLoading || !facilityName) ? (
                    <CampgroundPageLoading />
                ) : (
                    <CampgroundPageDesktop
                        facilityId={facilityId}
                        siteTypes={siteTypes}
                        typeOfUse={typeOfUse}
                        facilityRules={facilityRules}
                        seasonData={seasonData}
                        releaseDates={releaseDates}
                        facilityName={formatStateAbbreviations(toTitleCase(facilityName))}
                        facilityDescription={facilityDescription}
                        facilityNearLocation={facilityNearLocation}
                        rating={rating}
                        reviewCount={reviewCount}
                        mobileCoverageRating={cellAverage}
                        parentName={parentName}
                        parentUrl={parentId ? getGatewayUrl(parentId) : null}
                        searchQuery={searchQuery}
                        isLoadingStatuses={isLoadingStatuses}
                        navigateToSearchResults={navigateToSearchResultsVar}
                        isApplicationDuplicated={isApplicationDuplicated}
                        isApplicationInCart={isApplicationInCart}
                        isItinerary={isItinerary}
                    />
                )}
            </Suspense>
        </CampgroundPageProvider>
    );
}

// cSpell:ignore recarea, grnd, checkin
